'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faArrowRight } from '@fortawesome/free-solid-svg-icons';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const popularSearches = [
  { term: 'Pendaftaran 2024', link: '/pendaftaran' },
  { term: 'Biaya Kuliah', link: '/akademik' },
  { term: 'Beasiswa KIP', link: '/kemahasiswaan' },
  { term: 'Kalender Akademik', link: '/akademik' },
  { term: 'Fakultas Kedokteran', link: '/#akademik' },
  { term: 'Akreditasi Prodi', link: '/#stats' },
];

export default function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // In a real app, this would redirect to a search results page
      // router.push(`/search?q=${encodeURIComponent(searchTerm)}`);
      console.log('Searching for:', searchTerm);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-unmulDarkGreen/95 backdrop-blur-md flex flex-col animate-fade-in">
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white transition p-2 text-2xl"
        aria-label="Close search overlay"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className="flex-1 flex flex-col items-center justify-center p-6 container mx-auto max-w-4xl">
        {/* Logo */}
        <div className="mb-8 text-center text-white/80">
          <FontAwesomeIcon icon={faSearch} className="text-4xl mb-2" />
          <div className="text-sm font-medium tracking-wider uppercase">Pencarian</div>
        </div>

        {/* Search Input */}
        <form onSubmit={handleSearch} className="w-full relative mb-12">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Ketik kata kunci pencarian..."
            className="w-full bg-transparent border-b-2 border-white/20 text-white text-3xl md:text-5xl font-bold py-4 px-2 focus:outline-none focus:border-unmulYellow transition-colors placeholder-white/30 text-center"
          />
          <button 
            type="submit"
            className="absolute right-0 top-1/2 -translate-y-1/2 text-white/50 hover:text-unmulYellow transition text-2xl"
            aria-label="Submit search"
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </form>

        {/* Popular Searches */}
        <div className="text-center w-full">
          <h3 className="text-green-200 text-sm font-bold uppercase tracking-wider mb-6">Pencarian Populer</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {popularSearches.map((item, index) => (
              <Link 
                key={index}
                href={item.link}
                onClick={onClose}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full text-sm transition border border-white/10 hover:border-white/30 backdrop-blur-sm"
              >
                {item.term}
              </Link>
            ))}
          </div>
        </div>
      </div>
      
      <div className="p-6 text-center text-white/30 text-xs">
        Tekan ESC untuk menutup
      </div>
    </div>
  );
}
